import React, { useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import { WakeLockButton } from './GameRoom';
const SERVER = process.env.REACT_APP_SERVER_URL || '';
const KEYS = { ArrowUp: 4, ArrowDown: 5, ArrowLeft: 6, ArrowRight: 7, KeyZ: 0, KeyX: 8, KeyA: 1, KeyS: 9, ShiftLeft: 2, Enter: 3, KeyQ: 10, KeyW: 11 };
const B = { padding: '8px 12px', borderRadius: 7, border: '1px solid #555', background: '#26232c', color: '#fff', cursor: 'pointer' };
function token() {
  let t; try { t = sessionStorage.getItem('tavern-netplay-session'); } catch {}
  if (!/^[a-f0-9]{64}$/.test(t || '')) {
    t = [...crypto.getRandomValues(new Uint8Array(32))].map(b => b.toString(16).padStart(2, '0')).join('');
    try { sessionStorage.setItem('tavern-netplay-session', t); } catch {}
  }
  return t;
}
export default function NetplayRoom({ displayName }) {
  const [room, setRoom] = useState({ members: [], phase: 'empty' }), [me, setMe] = useState(null);
  const [error, setError] = useState(''), [busy, setBusy] = useState(false), [metrics, setMetrics] = useState(null), [focus, setFocus] = useState(false);
  const socket = useRef(), frame = useRef(), roomRef = useRef(room), meRef = useRef(null), tokenRef = useRef(token());
  const held = useRef(new Set()), padMask = useRef(0), loaded = useRef(false), panel = useRef();
  const post = (type, data = {}) => frame.current?.contentWindow?.postMessage({ tavernNetplay: true, type, data }, window.location.origin);
  const sendHeld = () => {
    let mask = padMask.current; held.current.forEach(b => { mask |= 1 << b; }); post('held', { mask });
  };
  const press = (b, value) => { value ? held.current.add(b) : held.current.delete(b); sendHeld(); };
  const init = () => {
    if (!loaded.current || !roomRef.current.game || !meRef.current) return;
    const m = roomRef.current.members.find(p => p.id === meRef.current);
    post('init', { epoch: roomRef.current.epoch, game: { ...roomRef.current.game, romUrl: SERVER + roomRef.current.game.romUrl }, host: roomRef.current.hostId === meRef.current, seat: m?.seat });
  };
  useEffect(() => {
    const s = io(SERVER + '/netplay', { path: '/socket.io', transports: ['websocket', 'polling'], reconnection: true });
    socket.current = s;
    const onRoom = r => {
      const previousSeat = roomRef.current.members.find(p => p.id === meRef.current)?.seat;
      roomRef.current = r; setRoom(r);
      const m = r.members.find(p => p.id === meRef.current);
      if (previousSeat !== m?.seat) { held.current.clear(); padMask.current = 0; post('held', { mask: 0 }); }
      post('config', { epoch: r.epoch, host: r.hostId === meRef.current, seat: m?.seat });
    };
    s.on('connect', () => {
      setError('');
      s.emit('np_join', { token: tokenRef.current, name: displayName, protocol: 4 }, result => {
        if (result.error) return setError(result.error);
        meRef.current = result.id; setMe(result.id); onRoom(result.room); init();
      });
    });
    s.on('np_room', onRoom);
    for (const event of ['np_pause', 'np_snapshot_request', 'np_snapshot', 'np_begin', 'np_frame']) s.on(event, data => post(event, data));
    s.on('np_pause', () => setMetrics(null));
    s.on('np_begin', () => setError(''));
    s.on('np_error', message => setError(String(message)));
    s.on('disconnect', () => { post('np_pause', { epoch: -1, reason: 'Disconnected; reconnecting…' }); setError('Disconnected; reconnecting…'); });
    s.on('connect_error', () => setError('Cannot connect to local netplay. Retry or use Streaming.'));
    const onMessage = event => {
      if (event.source !== frame.current?.contentWindow || event.origin !== window.location.origin || !event.data?.tavernNetplay) return;
      const { type, data } = event.data;
      if (type === 'frame_loaded') { loaded.current = true; init(); }
      else if (type === 'status') setMetrics(data);
      else if (type === 'key_input') press(data.button, data.value);
      else if (['np_ready', 'np_wait', 'np_runtime', 'np_snapshot', 'np_boot', 'np_input', 'np_check', 'np_error'].includes(type)) s.emit(type, data);
    };
    window.addEventListener('message', onMessage);
    return () => { window.removeEventListener('message', onMessage); s.disconnect(); loaded.current = false; };
    // Each mount owns one namespace socket. Room updates must not replace it or the iframe.
  }, [displayName]);
  useEffect(() => {
    const activate = () => { try { frame.current?.contentWindow?.tavernActivateAudio?.(); } catch {} };
    for (const event of ['pointerdown', 'touchend', 'click', 'keydown']) window.addEventListener(event, activate, { capture: true, passive: true });
    return () => { for (const event of ['pointerdown', 'touchend', 'click', 'keydown']) window.removeEventListener(event, activate, { capture: true }); };
  }, []);
  useEffect(() => {
    const release = () => { held.current.clear(); padMask.current = 0; sendHeld(); };
    const key = value => event => {
      if (/^(INPUT|TEXTAREA|SELECT)$/.test(event.target.tagName) || !roomRef.current.members.find(p => p.id === meRef.current)?.seat) return;
      const b = KEYS[event.code]; if (b !== undefined) { event.preventDefault(); press(b, value); }
    };
    const down = key(true), up = key(false);
    window.addEventListener('keydown', down); window.addEventListener('keyup', up); window.addEventListener('blur', release);
    document.addEventListener('visibilitychange', release);
    let raf;
    const poll = () => {
      let mask = 0;
      if (roomRef.current.members.find(p => p.id === meRef.current)?.seat) {
        const p = navigator.getGamepads?.()?.[0];
        if (p) {
          const map = { 0: 0, 1: 8, 2: 1, 3: 9, 4: 10, 5: 11, 8: 2, 9: 3, 12: 4, 13: 5, 14: 6, 15: 7 };
          Object.entries(map).forEach(([i, b]) => { if (p.buttons[i]?.pressed) mask |= 1 << b; });
          if (p.axes[0] < -0.35) mask |= 1 << 6; if (p.axes[0] > 0.35) mask |= 1 << 7;
          if (p.axes[1] < -0.35) mask |= 1 << 4; if (p.axes[1] > 0.35) mask |= 1 << 5;
        }
      }
      if (mask !== padMask.current) { padMask.current = mask; sendHeld(); }
      raf = requestAnimationFrame(poll);
    };
    raf = requestAnimationFrame(poll);
    return () => { cancelAnimationFrame(raf); release(); window.removeEventListener('keydown', down); window.removeEventListener('keyup', up); window.removeEventListener('blur', release); document.removeEventListener('visibilitychange', release); };
  }, []);
  const isHost = me && me === room.hostId, mySeat = room.members.find(p => p.id === me)?.seat;
  const action = (action, data = {}) => socket.current?.emit('np_action', { action, ...data });
  async function upload(file, state = false) {
    if (!file) return;
    setBusy(true); setError('');
    try {
      const response = await fetch(SERVER + (state ? '/netplay/save' : '/netplay/rom'), { method: 'POST',
        headers: { 'Content-Type': 'application/octet-stream', Authorization: 'Bearer ' + tokenRef.current, 'X-Game-Name': file.name },
        body: await file.arrayBuffer() });
      const result = await response.json(); if (!response.ok) throw new Error(result.error || 'Upload failed');
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  }
  async function download() {
    try {
      const response = await fetch(SERVER + '/netplay/save', { headers: { Authorization: 'Bearer ' + tokenRef.current } });
      if (!response.ok) throw new Error('Save a state first');
      const url = URL.createObjectURL(await response.blob()), a = document.createElement('a');
      a.href = url; a.download = 'tavern-snes.state'; a.click(); setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) { setError(err.message); }
  }
  return <section ref={panel} style={{ padding: 12, color: '#eee', background: '#111', ...(focus ? { position: 'fixed', inset: 0, zIndex: 10000, overflow: 'auto', boxSizing: 'border-box', paddingTop: 'env(safe-area-inset-top)' } : {}) }}>
    <h2 style={{ margin: '4px 0 8px', fontSize: 18 }}>Local netplay — experimental SNES</h2>
    <div style={{ fontSize: 12, color: '#bbb', marginBottom: 10 }}>Every browser renders locally. Two-frame input buffer; no compressed video. Sound starts automatically when permitted; if blocked, tap the game or any control to start it. Keep player tabs visible. {mySeat ? 'You control P' + mySeat + '.' : 'You are watching.'}</div>
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
      {isHost && <label style={B}>{busy ? 'Uploading…' : 'Upload SNES ROM'}<input aria-label="Upload SNES ROM" type="file" accept=".sfc,.smc,.swc,.zip" disabled={busy} onChange={e => { upload(e.target.files?.[0]); e.target.value = ''; }} style={{ display: 'none' }}/></label>}
      {isHost && room.game && <>
        <button style={B} onClick={() => action('refresh')}>Refresh room</button>
        <button style={B} onClick={() => action('save')}>Save state</button>
        <button style={B} disabled={!room.hasSave} onClick={() => action('load')}>Load state</button>
        <button style={B} disabled={!room.hasSave} onClick={download}>Download state</button>
        <label style={B}>Load state file<input type="file" aria-label="Load state file" accept=".state" onChange={e => { upload(e.target.files?.[0], true); e.target.value = ''; }} style={{ display: 'none' }}/></label>
        <button style={B} onClick={() => action('stop')}>Stop game</button>
      </>}
      <button style={B} onClick={() => setFocus(!focus)}>{focus ? 'Exit full screen' : 'Full screen'}</button>
      <WakeLockButton />
    </div>
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 10 }}>{[1, 2].map(seat =>
      <label key={seat}>Player {seat} <select aria-label={'Player ' + seat} disabled={!isHost} value={room.members.find(p => p.seat === seat)?.id || ''} style={{ ...B, padding: 5 }} onChange={e => action('seat', { seat, memberId: e.target.value || null })}>
        <option value="">Unassigned</option>{room.members.map(m => <option key={m.id} value={m.id}>{m.name}{m.id === room.hostId ? ' (host)' : ''}</option>)}
      </select></label>)}</div>
    {error && <div role="alert" style={{ color: '#ff9988', padding: 8 }}>{error}</div>}
    <div data-testid="netplay-status" style={{ fontSize: 12, color: room.phase === 'running' ? '#6f6' : '#ccc', marginBottom: 8 }}>
      {room.phase} · {room.game?.name || 'The first guest is host and can upload a SNES ROM.'}
      {metrics && ' · ' + Math.round(metrics.fps) + ' FPS · frame ' + metrics.frame + ' · audio ' + metrics.audio}
    </div>
    {room.game && <div style={{ width: '100%', maxWidth: 'min(100%, calc((100dvh - 260px) * 4 / 3))', aspectRatio: '4 / 3', margin: '0 auto', background: '#000' }}>
      <iframe key={room.game.id} ref={frame} title="Local SNES emulator" src={(SERVER || '') + '/netplay-player.html?game=' + room.game.id} allow="autoplay; fullscreen" style={{ width: '100%', height: '100%', display: 'block', border: 0 }}/>
    </div>}
    {room.game && mySeat && <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, justifyContent: 'center', padding: '10px 0', touchAction: 'none' }}>
      {[[4, '↑'], [6, '←'], [5, '↓'], [7, '→'], [0, 'B'], [8, 'A'], [1, 'Y'], [9, 'X'], [10, 'L'], [11, 'R'], [2, 'Select'], [3, 'Start']].map(([b, label]) =>
        <button key={b} aria-label={'P' + mySeat + ' ' + label} style={{ ...B, minWidth: 40, minHeight: 42, touchAction: 'none', userSelect: 'none' }}
          onPointerDown={e => { e.preventDefault(); e.currentTarget.setPointerCapture?.(e.pointerId); press(b, true); }}
          onPointerUp={() => press(b, false)} onPointerCancel={() => press(b, false)} onLostPointerCapture={() => press(b, false)}>{label}</button>)}
    </div>}
    <div style={{ fontSize: 11, color: '#999' }}>Keyboard: arrows, Z/X/A/S, Enter, Shift, Q/W. Physical gamepads supported. Save slots remain until the game/service stops; download a state to keep it.</div>
  </section>;
}
